import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FaHeart, FaPray, FaUsers, FaHandsHelping, FaStar, FaChurch } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f5ff 50%, #f0f7ff 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: ${props => props.themeMode === 'light' ? '#2d2d44' : '#fff'};
  overflow-x: hidden;
  transition: all 0.4s ease;
`;

const Header = styled.section`
  text-align: center;
  padding: 6rem 2rem 4rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, rgba(124, 77, 255, 0.08) 0%, rgba(83, 109, 254, 0.08) 100%)'
    : 'linear-gradient(135deg, rgba(106, 27, 154, 0.3) 0%, rgba(57, 73, 171, 0.3) 100%)'};
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem 3rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 800;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #7c4dff 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 50%, #ce93d8 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
  
  @media (max-width: 480px) {
    line-height: 1.6;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 10px 40px rgba(124, 77, 255, 0.1)'
    : 'none'};
  
  &:hover {
    transform: translateY(-10px);
    border-color: #7c4dff;
    box-shadow: 0 20px 40px rgba(124, 77, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 3rem;
  color: #7c4dff;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  margin-bottom: 1rem;
`;

const CardText = styled.p`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  line-height: 1.8;
  font-size: 1rem;
`;

const StorySection = styled.section`
  padding: 5rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.2)'};
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const StoryContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const StoryText = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  line-height: 2;
  margin-bottom: 2rem;
`;

const TeamSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const TeamCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 10px 30px rgba(124, 77, 255, 0.1)'
    : 'none'};
  
  &:hover {
    border-color: #7c4dff;
    transform: translateY(-5px);
  }
`;

const TeamPhoto = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  margin: 0 auto 1.5rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #fff;
  font-weight: bold;
`;

const TeamName = styled.h4`
  font-size: 1.3rem;
  color: ${props => props.themeMode === 'light' ? '#2d2d44' : '#b39ddb'};
  margin-bottom: 0.5rem;
`;

const TeamRole = styled.p`
  color: #7c4dff;
  font-size: 1rem;
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Scroll animation hook
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  return { ref, controls };
};

function About() {
  const { theme } = useTheme();
  const { data } = useData();
  const { t } = useLanguage();
  const themeMode = theme.mode;
  const valuesAnimation = useScrollAnimation();
  const storyAnimation = useScrollAnimation();
  const teamAnimation = useScrollAnimation();

  const values = [
    { icon: <FaPray />, title: 'Faith', text: 'We believe in the power of faith and trust in God\'s plan for our lives.' },
    { icon: <FaHeart />, title: 'Love', text: 'Love is at the center of everything we do, reflecting Christ\'s love to all.' },
    { icon: <FaUsers />, title: 'Community', text: 'We are stronger together, building meaningful relationships in Christ.' },
    { icon: <FaHandsHelping />, title: 'Service', text: 'Serving others is our calling, making a difference in our community.' },
    { icon: <FaStar />, title: 'Excellence', text: 'We strive for excellence in worship, ministry, and all we do.' },
    { icon: <FaChurch />, title: 'Worship', text: 'Passionate worship that honors God and transforms lives.' }
  ];

  // Get team from data context
  const team = data.aboutContent?.team || [
    { id: 1, name: 'Pastor Name', role: 'Senior Pastor', initials: 'PN', image: '' },
    { id: 2, name: 'Associate Pastor', role: 'Youth Ministry', initials: 'AP', image: '' },
    { id: 3, name: 'Worship Leader', role: 'Music Ministry', initials: 'WL', image: '' },
    { id: 4, name: 'Ministry Head', role: 'Outreach', initials: 'MH', image: '' }
  ];

  return (
    <Main themeMode={themeMode}>
      <Header themeMode={themeMode}>
        <Title
          themeMode={themeMode}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t.about.title}
        </Title>
        <Subtitle
          themeMode={themeMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {data.about?.description || t.about.subtitle}
        </Subtitle>
      </Header>

      {/* Our Values */}
      <Section>
        <motion.div
          ref={valuesAnimation.ref}
          initial="hidden"
          animate={valuesAnimation.controls}
          variants={staggerContainer}
        >
          <SectionTitle themeMode={themeMode} variants={fadeInUp}>Our Core Values</SectionTitle>
          <Grid>
            {values.map((value, index) => (
              <Card key={index} themeMode={themeMode} variants={fadeInUp} whileHover={{ scale: 1.02 }}>
                <IconWrapper>{value.icon}</IconWrapper>
                <CardTitle themeMode={themeMode}>{value.title}</CardTitle>
                <CardText themeMode={themeMode}>{value.text}</CardText>
              </Card>
            ))}
          </Grid>
        </motion.div>
      </Section>

      {/* Our Story */}
      <StorySection themeMode={themeMode}>
        <motion.div
          ref={storyAnimation.ref}
          initial="hidden"
          animate={storyAnimation.controls}
          variants={staggerContainer}
        >
          <StoryContent>
            <SectionTitle themeMode={themeMode} variants={fadeInUp}>Our Story</SectionTitle>
            <StoryText themeMode={themeMode} variants={fadeInUp}>
              House Of Worship Davao was founded with a simple yet profound mission: 
              to create a place where everyone can experience God's love and grace. 
              From humble beginnings, we have grown into a thriving community of 
              believers who are passionate about worship, discipleship, and service.
            </StoryText>
            <StoryText themeMode={themeMode} variants={fadeInUp}>
              "Where would we be if not for God's Grace" - This is our living testimony! 
              Every day, we witness the transformative power of God in the lives of our 
              members and the community we serve. Our desire is to help you discover 
              Jesus, His promises, and His glorious plan for your life.
            </StoryText>
          </StoryContent>
        </motion.div>
      </StorySection>

      {/* Our Team */}
      <TeamSection>
        <motion.div
          ref={teamAnimation.ref}
          initial="hidden"
          animate={teamAnimation.controls}
          variants={staggerContainer}
        >
          <SectionTitle themeMode={themeMode} variants={fadeInUp}>Our Leadership</SectionTitle>
          <TeamGrid>
            {team.map((member, index) => (
              <TeamCard key={member.id || index} themeMode={themeMode} variants={fadeInUp} whileHover={{ scale: 1.05 }}>
                <TeamPhoto image={member.image}>{!member.image && member.initials}</TeamPhoto>
                <TeamName themeMode={themeMode}>{member.name}</TeamName>
                <TeamRole>{member.role}</TeamRole>
              </TeamCard>
            ))}
          </TeamGrid>
        </motion.div>
      </TeamSection>
    </Main>
  );
}

export default About;
